<template>
  <div class="panel-screen">
    <panel-toolbar :period="period" show-period @update:period="changePeriod" />

    <!-- Conta que nao e de influencer cai aqui: mostrar a razao e melhor que uma tela
         vazia sem explicacao. -->
    <b-alert v-if="fatalError" variant="warning" show class="p-1">
      <small>{{ fatalError }}</small>
    </b-alert>

    <template v-else>
      <b-skeleton-wrapper :loading="loadingProfile">
        <template #loading>
          <hb-section><b-skeleton width="45%" /><b-skeleton width="70%" /></hb-section>
        </template>
        <profile-header v-if="profile" :profile="profile" @generate-link="openGenerateLink" />
      </b-skeleton-wrapper>

      <b-skeleton-wrapper :loading="loadingDashboard">
        <template #loading>
          <hb-section><b-skeleton-img no-aspect height="180px" /></hb-section>
        </template>

        <template v-if="data">
          <wallet-summary-card
            :balances="data.balances"
            :upcoming="data.upcoming"
            :title="$t('influencer_panel.wallet_title')"
            show-total
            link-to-statement
            @withdraw="goToWithdraw"
          />

          <performance-strip
            :metrics="data.metrics"
            :previous="data.previous"
            :title="performanceTitle"
            :subtitle="data.previous ? $t('influencer_panel.compared_to_previous') : ''"
          />

          <!-- Sem nenhuma comissao no periodo o grafico seria uma linha reta no zero:
               nao informa nada e ocupa a dobra. -->
          <commission-chart
            v-if="hasSeries"
            :current="data.series.current"
            :previous="data.series.previous"
            :current-label="periodLabelText"
            :money-hidden="moneyHidden"
          />

          <div class="panel-screen__pair">
            <!-- Cada um destes some sozinho quando não há o que mostrar: funil sem
                 clique nenhum e canais sem venda não informam nada. -->
            <conversion-funnel v-if="hasFunnel" :steps="data.funnel" />
            <sales-by-channel v-if="hasChannels" :channels="data.channels" />
          </div>
        </template>
      </b-skeleton-wrapper>

      <section-error v-if="dashboardError" :message="dashboardError" @retry="loadDashboard" />

      <b-skeleton-wrapper :loading="loadingCampaigns">
        <template #loading>
          <hb-section><b-skeleton-img no-aspect height="140px" /></hb-section>
        </template>
        <campaign-compact-list
          v-if="!campaignsError"
          :participations="activeParticipations"
          :total-sales="data ? data.metrics.conversions : 0"
          @generate-link="openGenerateLink"
          @material="openMaterial"
          @info="openInfo"
        />
      </b-skeleton-wrapper>

      <section-error v-if="campaignsError" :message="campaignsError" @retry="loadCampaigns" />

      <!-- Mercado Hubee. Endpoint próprio e carregamento próprio: é a consulta mais cara
           do painel e não pode segurar os saldos. Falha em silêncio — é contexto, não o
           dado que o influencer veio buscar. -->
      <market-section
        v-if="market"
        :positioning="market.positioning"
        :timing="market.timing"
        :opportunities="market.opportunities"
      />
    </template>

    <generate-link-modal
      ref="generateLink"
      :participations="joinedParticipations"
      :preselected="linkTarget"
      @created="loadCampaigns"
    />

    <campaign-material-modal ref="material" :participation="materialTarget" />
    <campaign-info-modal ref="info" />
  </div>
</template>

<script>
import { BAlert, BSkeleton, BSkeletonImg, BSkeletonWrapper } from 'bootstrap-vue'
import { mapGetters } from 'vuex'
import InfluencerPanelService from '@/services/api/InfluencerPanelService'
import HbSection from '@/components/hubee/HbSection.vue'
import PanelToolbar from './components/PanelToolbar.vue'
import ProfileHeader from './components/ProfileHeader.vue'
import WalletSummaryCard from './components/WalletSummaryCard.vue'
import PerformanceStrip from './components/PerformanceStrip.vue'
import CommissionChart from './components/CommissionChart.vue'
import CampaignCompactList from './components/CampaignCompactList.vue'
import ConversionFunnel from './components/ConversionFunnel.vue'
import SalesByChannel from './components/SalesByChannel.vue'
import MarketSection from './components/MarketSection.vue'
import GenerateLinkModal from './components/GenerateLinkModal.vue'
import CampaignMaterialModal from './components/CampaignMaterialModal.vue'
import CampaignInfoModal from './components/CampaignInfoModal.vue'
import SectionError from './components/SectionError.vue'
import {
  DEFAULT_PERIOD, PERIODS, periodParams, periodLabel, extractMessage,
} from './panel'

/**
 * Resumo do painel do influencer.
 *
 * Cada recurso tem o seu proprio par carregando/erro em vez de um unico overlay: assim o
 * cabecalho de perfil pinta enquanto o grafico ainda busca, e um endpoint que falha
 * derruba so a sua secao.
 */
export default {
  components: {
    BAlert,
    BSkeleton,
    BSkeletonImg,
    BSkeletonWrapper,
    HbSection,
    PanelToolbar,
    ProfileHeader,
    WalletSummaryCard,
    PerformanceStrip,
    CommissionChart,
    CampaignCompactList,
    ConversionFunnel,
    SalesByChannel,
    MarketSection,
    GenerateLinkModal,
    CampaignMaterialModal,
    CampaignInfoModal,
    SectionError,
  },
  data: () => ({
    service: null,
    profile: null,
    data: null,
    market: null,
    participations: [],
    period: DEFAULT_PERIOD,
    loadingProfile: true,
    loadingDashboard: true,
    loadingCampaigns: true,
    fatalError: '',
    dashboardError: '',
    campaignsError: '',
    linkTarget: null,
    materialTarget: null,
  }),
  computed: {
    ...mapGetters('moneyVisibility', ['moneyHidden']),
    periodLabelText() {
      return periodLabel(this.period)
    },
    performanceTitle() {
      return this.$t('influencer_panel.performance_in', { period: this.periodLabelText })
    },
    hasSeries() {
      return !!this.data
        && this.data.series.current.some(point => Number(point.commission_cents) > 0)
    },
    hasFunnel() {
      return !!this.data && (this.data.funnel || []).some(step => step.count > 0)
    },
    hasChannels() {
      return !!this.data && (this.data.channels || []).length > 0
    },
    joinedParticipations() {
      return this.participations.filter(p => ['AC', 'PU'].includes(p.status))
    },
    activeParticipations() {
      return this.joinedParticipations.slice(0, 5)
    },
  },
  beforeMount() {
    this.service = InfluencerPanelService.build(this.$vs)

    const fromQuery = this.$route.query.period
    if (PERIODS.includes(fromQuery)) this.period = fromQuery
  },
  mounted() {
    this.loadProfile()
    this.loadDashboard()
    this.loadCampaigns()
    this.loadMarket()
  },
  methods: {
    loadProfile() {
      this.loadingProfile = true

      return this.service.profile()
        .then(profile => { this.profile = profile })
        .catch(error => { this.fatalError = extractMessage(error) })
        .finally(() => { this.loadingProfile = false })
    },
    loadDashboard() {
      this.loadingDashboard = true
      this.dashboardError = ''

      return this.service.dashboard(periodParams(this.period))
        .then(data => { this.data = data })
        .catch(error => { this.dashboardError = extractMessage(error) })
        .finally(() => { this.loadingDashboard = false })
    },
    loadCampaigns() {
      this.loadingCampaigns = true
      this.campaignsError = ''

      return this.service.myCampaigns(periodParams(this.period))
        .then(list => { this.participations = list || [] })
        .catch(error => { this.campaignsError = extractMessage(error) })
        .finally(() => { this.loadingCampaigns = false })
    },
    loadMarket() {
      return this.service.market(periodParams(this.period))
        .then(market => { this.market = market })
        .catch(() => {
          // Sem alarme: o Mercado é contexto, não o dado que o influencer veio buscar.
          // O bloco some e o resto da tela segue igual.
          this.market = null
        })
    },
    changePeriod(period) {
      this.period = period
      // Na URL para que recarregar a pagina (ou compartilhar o link) preserve o recorte.
      this.$router.replace({ query: { ...this.$route.query, period } }).catch(() => {})
      this.loadDashboard()
      this.loadCampaigns()
      this.loadMarket()
    },
    openGenerateLink(participation = null) {
      this.linkTarget = participation ? participation.id : null
      this.$refs.generateLink.open(this.linkTarget)
    },
    openMaterial(participation) {
      this.materialTarget = participation
      this.$refs.material.open(participation)
    },
    openInfo(participation) {
      this.$refs.info.open(participation)
    },
    goToWithdraw() {
      this.$router.push({ name: 'influencer-panel-wallet', query: { tab: 'saques', withdraw: '1' } })
        .catch(() => {})
    },
  },
}
</script>

<style scoped>
.panel-screen {
  display: flex;
  flex-direction: column;
  gap: clamp(1rem, 1.8vw, 1.5rem);
}

.panel-screen__pair {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: clamp(1rem, 1.8vw, 1.5rem);
}
</style>
